import { useState, useEffect } from 'react'
import { Instagram, MessageCircle } from 'lucide-react'

// TikTok icon (lucide doesn't have one)
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
)

// X (formerly Twitter) icon
const XIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
)

// Discord icon
const DiscordIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
  </svg>
)

const PLATFORMS = [
  {
    key: 'instagram',
    label: 'Instagram',
    icon: Instagram,
    prefix: '@',
    placeholder: 'username'
  },
  {
    key: 'tiktok',
    label: 'TikTok',
    icon: TikTokIcon,
    prefix: '@',
    placeholder: 'username'
  },
  {
    key: 'snapchat',
    label: 'Snapchat',
    icon: MessageCircle,
    prefix: '',
    placeholder: 'username'
  },
  {
    key: 'twitter',
    label: 'X',
    icon: XIcon,
    prefix: '@',
    placeholder: 'handle'
  },
  {
    key: 'discord',
    label: 'Discord',
    icon: DiscordIcon,
    prefix: '',
    placeholder: 'username'
  }
]

// Validation patterns
const PATTERNS = {
  instagram: /^[a-zA-Z0-9._]{1,30}$/,
  tiktok: /^[a-zA-Z0-9._]{1,24}$/,
  snapchat: /^[a-zA-Z][a-zA-Z0-9._-]{2,14}$/,
  twitter: /^[a-zA-Z0-9_]{1,15}$/,
  discord: /^.{2,32}$/
}

const SocialHandlesEditor = ({
  handles = {},
  onChange,
  showToggle = true,
  showOnProfile = false,
  onToggleVisibility
}) => {
  const [localHandles, setLocalHandles] = useState(handles || {})
  const [errors, setErrors] = useState({})

  useEffect(() => {
    setLocalHandles(handles || {})
  }, [handles])

  const handleChange = (platform, value) => {
    const cleanValue = value.replace(/^@/, '').trim()

    const newHandles = {
      ...localHandles,
      [platform]: cleanValue || undefined
    }

    // Remove empty values
    Object.keys(newHandles).forEach((key) => {
      if (!newHandles[key]) delete newHandles[key]
    })

    // Validate
    const newErrors = { ...errors }
    if (cleanValue && PATTERNS[platform] && !PATTERNS[platform].test(cleanValue)) {
      newErrors[platform] = 'Invalid format'
    } else {
      delete newErrors[platform]
    }

    setErrors(newErrors)
    setLocalHandles(newHandles)
    onChange(
      Object.keys(newHandles).length > 0 ? newHandles : null,
      Object.keys(newErrors).length === 0
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Social Media</h4>
        {showToggle && (
          <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
            <input
              type="checkbox"
              checked={showOnProfile}
              onChange={(e) => onToggleVisibility(e.target.checked)}
              className="rounded border-gray-300 text-primary-500 focus:ring-primary-500"
            />
            Show on profile
          </label>
        )}
      </div>

      <div className="space-y-3">
        {PLATFORMS.map(({ key, label, icon: Icon, prefix, placeholder }) => (
          <div key={key}>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <Icon className="w-4 h-4" />
              </div>
              <input
                type="text"
                placeholder={`${prefix}${placeholder}`}
                value={localHandles[key] ? `${prefix}${localHandles[key]}` : ''}
                onChange={(e) => handleChange(key, e.target.value)}
                className={`w-full pl-10 pr-4 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors ${
                  errors[key]
                    ? 'border-error-300 focus:ring-error-500 focus:border-error-500'
                    : 'border-gray-300 dark:border-gray-600 focus:ring-primary-500 focus:border-primary-500'
                }`}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                {label}
              </span>
            </div>
            {errors[key] && <p className="text-xs text-error-500 mt-1">{errors[key]}</p>}
          </div>
        ))}
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400">
        Add your social handles to connect with other students. All handles are optional.
      </p>
    </div>
  )
}

export default SocialHandlesEditor
